import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import Logo from "../components/Logo";
import ResultCard from "../components/ResultCard";
import Button from "../components/Button";
import { TestResult } from "../types";
import { calculateResult } from "../utils/scoringUtils";
import { questions } from "../data/questions";
import ResultsFooter from "../components/ResultsFooter";

const SharedResultsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [result, setResult] = useState<TestResult | null>(null);
  const [percentages, setPercentages] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSharedResult = async () => {
      try {
        const testResultId = searchParams.get("id");

        if (!testResultId) {
          throw new Error("No test result ID provided");
        }

        const { data: testData, error: testError } = await supabase
          .from("test_data_view")
          .select("*")
          .eq("test_result_id", testResultId)
          .single();

        if (testError) {
          throw testError;
        }

        if (!testData) {
          throw new Error("Test result not found");
        }

        const scores = {
          jaguar: testData.jaguar_score,
          condor: testData.mariposa_score || testData.condor_score || 0,
          llama: testData.llama_score,
          quetzal: testData.quetzal_score,
          colibri: testData.colibri_score,
          coqui: testData.coqui_score,
          buho: testData.buho_score,
        };

        const calculatedResult = calculateResult(scores, questions, []);
        const resultWithMetadata: TestResult = {
          ...calculatedResult,
          testResultId: testData.test_result_id,
          userName: `${testData.first_name} ${testData.last_name}`,
        };

        setResult(resultWithMetadata);
        // Use the calculated percentages directly from the result
        setPercentages({ ...calculatedResult.scores });
      } catch (err) {
        console.error("Error fetching shared result:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load shared results"
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchSharedResult();
  }, [searchParams]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando resultados compartidos...</p>
        </div>
      </div>
    );
  }

  if (error || !result) {
    return (
      <div className="min-h-screen bg-orange-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            {error || "Results not found"}
          </h2>
          <Button onClick={() => (window.location.href = "/")}>
            Haz la prueba
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-orange-50 py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <header className="flex justify-center mb-8">
            <Logo size="medium" />
          </header>

          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Resultados Compartidos de Test Latino™
            </h1>
            <p className="text-gray-600">
              {result.userName} compartió sus resultados de Test Latino™ contigo
            </p>
          </div>

          <div className="mb-10">
            <ResultCard result={result} percentages={percentages} />
          </div>

          <div className="text-center">
            <Button onClick={() => (window.location.href = "/")}>
              Haz la prueba
            </Button>
          </div>

          <ResultsFooter />
        </div>
      </div>
    </>
  );
};

export default SharedResultsPage;
